import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal/modal';
import { Button, CircularProgress, Grid } from '@material-ui/core';
import axios from '../../axios-default';
import { ApiServerUrl } from '../../config.json';
import urljoin from 'url-join';
import { useLang } from '../../util/lang';
import ImageMagnify from '../ImageMagnify/ImageMagnify';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { easySnackbar } from '../../store/Noti/noti.actions';
import { IMAGE_CONTAINER } from '../CaseSupportingFileInput/CaseSupportingFileInput';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/**
 * @typedef {Object} CaseSupportingFileData
 * @property {number} case_activity_log_id - The case activity log id.
 * @property {number} case_id - The case id.
 * @property {number} case_supporting_file_id - The case supporting file id.
 * @property {string} file - The file path or URL.
 * @property {string} subject - The subject or description of the file.
 * @property {number} created_by - user id of the person who created the file.
 * @property {string} created_date - The subject or description of the file.
 * @property {number} updated_by - user id of the person who updated the file.
 * @property {string} updated_date - The subject or description of the file.
 */

const ViewCaseSupportingFileModal = ({
    open = false,
    caseActivityLogId = null,
    handleClose,
    caseId = null,
    caseLogCreatedDate,
    caseLogStatus,
}) => {
    /**
     * @type {[CaseSupportingFileData[], React.Dispatch<React.SetStateAction<CaseSupportingFileData[]>>]}
     */
    const [savedCaseSupportingFiles, setSavedCaseSupportingFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const get = useLang();
    const dispatch = useDispatch();

    const modalTitle = useMemo(() => {
        let baseTitle = get('@PatientInfo.log.REMINDER_DETAILS');
        if (caseLogStatus && caseLogStatus.length > 0) {
            if (['AWAITING', 'RETRACK_AWAITING'].includes(caseLogStatus)) {
                baseTitle = get('@PatientInfo.log.VIEW_DETAILS');
            }
        }
        return baseTitle;
    }, [caseLogStatus]);

    const formattedDate = useMemo(() => {
        return caseLogCreatedDate ? moment(caseLogCreatedDate).format('DD-MMM-YYYY HH:mm:ss') : '';
    }, [caseLogCreatedDate]);

    useEffect(() => {
        const getCaseActivityFile = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`/case/caseSupportingFile/${caseActivityLogId}`);
                if (res.data && res.status === 200) {
                    const { data } = res.data;
                    setSavedCaseSupportingFiles(data);
                }
            } catch (error) {
                dispatch(easySnackbar(error?.message, 'error'));
            } finally {
                setLoading(false);
            }
        };

        if (caseActivityLogId) {
            getCaseActivityFile();
        } else {
            setSavedCaseSupportingFiles([]);
        }

        return () => {
            setSavedCaseSupportingFiles([]);
            setLoading(false);
        };
    }, [caseActivityLogId]);

    const getFileName = (filePath = '') => {
        const parts = filePath.split('/');
        const lastPart = parts[parts.length - 1];
        let downloadFileName = lastPart;
        if (caseId) {
            downloadFileName = lastPart.replace('case_', `case${caseId}_`);
        }
        return downloadFileName;
    };

    const handleDownloadFiles = async () => {
        try {
            const zip = new JSZip();
            for (let index = 0; index < savedCaseSupportingFiles.length; index++) {
                const data = savedCaseSupportingFiles[index];
                const filePath = urljoin(ApiServerUrl, data.file);
                const downloadFileName = getFileName(filePath);
                const res = await axios.get(filePath, { responseType: 'blob' });
                if (res.status !== 200 || !res.data) {
                    throw new Error(`Failed to download file`);
                }
                // Create a Blob from the response data
                const blob = new Blob([res.data]);
                zip.file(downloadFileName, blob);
            }
            const caseLogDate = caseLogCreatedDate ? moment(caseLogCreatedDate).format('DD-MMM-YYYY') : '';
            let caseIndicator = '';
            if (caseId && caseLogDate.length > 0 && caseActivityLogId) {
                caseIndicator = `${caseId}_${caseLogDate}_${caseActivityLogId}`;
            }
            const zipName = `case${caseIndicator}_supporting_files.zip`;
            // Generate the zip file and trigger download
            zip.generateAsync({ type: 'blob' }).then((content) => {
                saveAs(content, zipName);
            });
        } catch (error) {
            dispatch(easySnackbar(error?.message, 'error'));
        }
    };

    return (
        <Modal open={open} handleClose={handleClose} title={modalTitle}>
            <Grid>
                {loading && (
                    <Grid container style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress style={{ color: 'rgb(6,26,46)' }} />
                    </Grid>
                )}
                {!loading && savedCaseSupportingFiles.length > 0 && (
                    <>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <h4>{formattedDate}</h4>
                            <div>
                                <Button variant="contained" color="secondary" onClick={handleDownloadFiles}>
                                    {get('@PatientInfo.modals.download')}
                                </Button>
                            </div>
                        </div>
                        {savedCaseSupportingFiles.map((data, index) => {
                            const filePath = urljoin(ApiServerUrl, data.file);
                            return (
                                <div key={index}>
                                    <h4>{data?.subject || ''}</h4>
                                    <ImageMagnify data_url={filePath} smallImage={{ height: IMAGE_CONTAINER.height }} />
                                    <hr />
                                </div>
                            );
                        })}
                    </>
                )}

                {!loading && !savedCaseSupportingFiles.length && (
                    <>
                        <h4>
                            No file to show for case activity log: <span>{caseActivityLogId || ''}</span>
                        </h4>
                    </>
                )}
            </Grid>
        </Modal>
    );
};

export default ViewCaseSupportingFileModal;
